import React, { useMemo, useRef } from 'react';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';

const superCleanString = (str) => {
    if (!str) return '';
    return str
        .toString()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/đ/g, 'd')
        .replace(/Đ/g, 'D')
        .replace(/\s+/g, ' ')
        .toLowerCase()
        .trim();
};

const SearchableFocusDropdown = ({ value, options, onChange, placeholder, disabled, panelUniqueClass, className, itemTemplate }) => {
    const dropdownRef = useRef(null);
    const focusTrapRef = useRef(null);

    const handleShow = () => {
        setTimeout(() => {
            try {
                const panel = document.querySelector(`.${panelUniqueClass}`);
                if (panel) {
                    const filterInput = panel.querySelector('.p-dropdown-filter');
                    if (filterInput && typeof filterInput.focus === 'function') {
                        filterInput.focus();

                        focusTrapRef.current = (e) => {
                            if (e.target !== filterInput) {
                                filterInput.focus();
                            }
                        };

                        panel.addEventListener('focusin', focusTrapRef.current);
                    }
                }
            } catch (err) {
                console.warn("Focus error:", err);
            }
        }, 100);
    };

    const handleHide = () => {
        const panel = document.querySelector(`.${panelUniqueClass}`);
        if (panel && focusTrapRef.current) {
            panel.removeEventListener('focusin', focusTrapRef.current);
        }
    };

    return (
        <Dropdown
            ref={dropdownRef}
            value={value}
            options={options}
            onChange={onChange}
            filter
            filterBy="label,searchKey"
            placeholder={placeholder}
            className={className || "custom-dropdown"}
            panelClassName={panelUniqueClass}
            style={{ width: '100%' }}
            appendTo={document.body}
            onShow={handleShow}
            onHide={handleHide}
            disabled={disabled}
            itemTemplate={itemTemplate}
            emptyFilterMessage="Không tìm thấy kết quả"
        />
    );
};

const BookCompilationForm = ({
    isOpen,
    onClose,
    onSubmit,
    formData,
    setFormData,
    isEditing,
    isReadOnly,
    projectTypes,
    members = [],
    setMembers,
    users = [],
    toast
}) => {

    const userOptions = useMemo(() => {
        return users.map(u => {
            const fullName = `${u.LastName || ''} ${u.FirstName || ''}`.trim();
            const email = u.Email || u.email || '';
            const dept = u.DepartmentName || '';
            const displayLabel = email ? `${fullName} - ${email}` : fullName;
            const searchStr = superCleanString(`${fullName} ${email} ${dept}`);

            return {
                label: displayLabel,
                value: u.Id,
                searchKey: searchStr,
                fullName: fullName,
                email: email,
                dept: dept
            };
        });
    }, [users]);

    const userItemTemplate = (option) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', padding: '4px 0' }}>
                <span style={{ fontWeight: 'bold', color: '#333' }}>{option.fullName}</span>
                <span style={{ fontSize: '12px', color: '#666' }}>
                    {option.email} {option.dept ? ` - ${option.dept}` : ''}
                </span>
            </div>
        );
    };

    const parsedFiles = useMemo(() => {
        if (!formData.AttachmentUrl) return [];
        try {
            if (formData.AttachmentUrl.startsWith('[')) {
                return JSON.parse(formData.AttachmentUrl);
            }
            if (formData.AttachmentUrl.includes('|')) {
                const parts = formData.AttachmentUrl.split('|');
                return [{ name: parts[0], data: parts[1], description: '' }];
            }
        } catch (e) {
            return [];
        }
        return [];
    }, [formData.AttachmentUrl]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        if (isReadOnly) return;
        const { name, value, type, checked } = e.target;
        let finalValue = value;
        if (type === 'checkbox') finalValue = checked;
        else if (type === 'number') finalValue = value === '' ? '' : Number(value);

        setFormData({ ...formData, [name]: finalValue });
    };

    const handleDateChange = (name, dateObj) => {
        if (isReadOnly) return;
        let dateString = '';
        if (dateObj) {
            const yyyy = dateObj.getFullYear();
            const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
            const dd = String(dateObj.getDate()).padStart(2, '0');
            dateString = `${yyyy}-${mm}-${dd}`;
        }
        handleChange({ target: { name, value: dateString, type: 'text' } });
    };

    const handleTitleBlur = async (e) => {
        if (isReadOnly) return;
        const titleText = e.target.value;
        if (!titleText || titleText.trim().length < 5) return;

        try {
            const userStr = localStorage.getItem('user');
            const currentUser = userStr ? JSON.parse(userStr) : null;
            const userId = currentUser ? currentUser.Id : '';

            const userRoleName = (currentUser?.RoleName || '').toLowerCase();
            const isManager = userRoleName === 'admin' || currentUser?.RoleId === 1 ||
                (userRoleName.includes('trưởng khoa') && currentUser?.IsDelegatedAdmin === true);

            const baseUrl = process.env.REACT_APP_API_BASE_URL;
            const response = await fetch(`${baseUrl}/bookcompilation?userId=${userId}&isAdmin=${isManager}`, {
                headers: {
                    'X-User-Role-Id': currentUser?.RoleId?.toString() || '',
                    'X-User-Dept-Id': currentUser?.DepartmentId?.toString() || ''
                }
            });

            if (response.ok) {
                const existingData = await response.json();

                const normalizeString = (str) => {
                    if (!str) return '';
                    return str.toLowerCase().trim()
                        .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                        .replace(/đ/g, 'd').replace(/Đ/g, 'D')
                        .replace(/[^a-z0-9]/g, "");
                };

                const calculateSimilarity = (s, t) => {
                    if (!s || !t) return 0;
                    if (s === t) return 1;
                    const d = Array(s.length + 1).fill(null).map(() => Array(t.length + 1).fill(null));
                    for (let i = 0; i <= s.length; i++) d[i][0] = i;
                    for (let j = 0; j <= t.length; j++) d[0][j] = j;
                    for (let j = 1; j <= t.length; j++) {
                        for (let i = 1; i <= s.length; i++) {
                            const cost = s[i - 1] === t[j - 1] ? 0 : 1;
                            d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
                        }
                    }
                    return 1.0 - (d[s.length][t.length] / Math.max(s.length, t.length));
                };

                const normInput = normalizeString(titleText);
                const currentId = isEditing ? formData.Id : 0;
                const inputYear = formData.StartDate ? formData.StartDate.substring(0, 4) : '';

                for (let item of existingData) {
                    if (item.Id === currentId) continue;

                    const normExisting = normalizeString(item.BookName);
                    const sim = calculateSimilarity(normInput, normExisting);
                    const existingYear = item.StartDate ? item.StartDate.substring(0, 4) : '';

                    if (sim > 0.90) {
                        if (sim > 0.98 || existingYear === inputYear || (formData.ISBN && item.ISBN === formData.ISBN)) {
                            toast?.current?.show({
                                severity: 'warn',
                                summary: 'Cảnh báo trùng lặp',
                                detail: `Hệ thống phát hiện Đăng ký biên soạn Sách/GT rất giống đã tồn tại: "${item.BookName}". Vui lòng kiểm tra lại!`,
                                life: 8000
                            });
                            break;
                        }
                    }
                }
            }
        } catch (err) {
            console.error("Lỗi check trùng lặp:", err);
        }
    };

    const updateEvidenceFiles = (newFiles) => {
        setFormData({ ...formData, AttachmentUrl: JSON.stringify(newFiles) });
    };

    const handleMultiFileChange = (e) => {
        if (isReadOnly) return;

        const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png'];
        const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];
        const MAX_FILE_SIZE = 5 * 1024 * 1024;

        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        const validFiles = files.filter(file => {
            const isTypeValid = allowedTypes.includes(file.type);
            const fileName = file.name.toLowerCase();
            const isExtensionValid = allowedExtensions.some(ext => fileName.endsWith(ext));

            if (!isTypeValid && !isExtensionValid) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Định dạng không hợp lệ',
                    detail: `Tệp "${file.name}" không được hỗ trợ. Chỉ cho phép PDF, JPG/JPEG, PNG`,
                    life: 5000
                });
                return false;
            }

            if (file.size > MAX_FILE_SIZE) {
                toast?.current?.show({
                    severity: 'warn',
                    summary: 'Dung lượng quá lớn',
                    detail: `Tệp "${file.name}" vượt quá giới hạn 5MB. Vui lòng chọn tệp nhỏ hơn.`,
                    life: 5000
                });
                return false;
            }

            return true;
        });

        if (validFiles.length === 0) {
            e.target.value = null;
            return;
        }

        const newFilesPromises = validFiles.map(file => new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve({ name: file.name, data: reader.result, description: '' });
            reader.readAsDataURL(file);
        }));

        Promise.all(newFilesPromises).then(newFiles => {
            updateEvidenceFiles([...parsedFiles, ...newFiles]);
        });
        e.target.value = null;
    };

    const handleFileDescChange = (index, desc) => {
        const updated = [...parsedFiles];
        updated[index].description = desc;
        updateEvidenceFiles(updated);
    };

    const removeEvidenceFile = (index) => {
        const updated = parsedFiles.filter((_, i) => i !== index);
        updateEvidenceFiles(updated);
    };

    const downloadIndividualFile = async (fileObj) => {
        let fileUrl = fileObj.url || fileObj.data || fileObj.link || fileObj.Link || fileObj.URL;
        if (!fileUrl) {
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không tìm thấy đường dẫn tệp!', life: 3000 });
            return;
        }

        const base64ToBlob = (dataUrl) => {
            const arr = dataUrl.split(',');
            const mime = arr[0].match(/:(.*?);/)?.[1] || 'application/octet-stream';
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8arr = new Uint8Array(n);
            while (n--) {
                u8arr[n] = bstr.charCodeAt(n);
            }
            return new Blob([u8arr], { type: mime });
        };

        if (typeof fileUrl === 'string') {
            fileUrl = fileUrl.trim();
            if (fileUrl.startsWith('/due-bucket') || fileUrl.startsWith('/uploads')) {
                fileUrl = `https://data.tapchikhkt.vn${fileUrl}`;
            }
        }

        try {
            if (typeof fileUrl === 'string' && fileUrl.startsWith('data:')) {
                const blob = base64ToBlob(fileUrl);
                const objectUrl = URL.createObjectURL(blob);

                const newWindow = window.open(objectUrl, '_blank');
                if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                    const link = document.createElement('a');
                    link.href = objectUrl;
                    link.target = '_blank';
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                }

                setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
                return;
            }

            const newWindow = window.open(fileUrl, '_blank');
            if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                const link = document.createElement('a');
                link.href = fileUrl;
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }
        } catch (error) {
            console.error("Lỗi khi xử lý mở file:", error);
            toast?.current?.show({ severity: 'error', summary: 'Lỗi', detail: 'Không thể hiển thị tệp này do lỗi định dạng!', life: 3000 });
        }
    };

    const handleAddMember = () => setMembers([...members, { userId: '', name: '', role: '', title: '', degree: '', content: '' }]);

    const handleMemberChange = (index, field, value) => {
        if (isReadOnly) return;
        const newMembers = [...members];
        newMembers[index][field] = value;
        setMembers(newMembers);
    };

    const handleRemoveMember = (index) => {
        if (isReadOnly) return;
        const newMembers = members.filter((_, i) => i !== index);
        setMembers(newMembers);
    };

    const handleMemberSelect = (index, userId) => {
        const selectedUser = users.find(u => u.Id === parseInt(userId));
        const newMembers = [...members];
        if (selectedUser) {
            newMembers[index] = {
                ...newMembers[index],
                userId: selectedUser.Id,
                name: `${selectedUser.LastName} ${selectedUser.FirstName}`,
                title: selectedUser.AcademicTitleName || '',
                degree: selectedUser.DegreeName || ''
            };
        } else {
            newMembers[index] = { ...newMembers[index], userId: '', name: '', title: '', degree: '' };
        }
        setMembers(newMembers);
    };

    const currentUserStr = localStorage.getItem('user');
    const currentUserInfo = currentUserStr ? JSON.parse(currentUserStr) : null;

    const handleLocalSubmit = (e) => {
        e.preventDefault();
        if (isReadOnly) return;

        if (parsedFiles.length === 0) {
            toast?.current?.show({
                severity: 'warn',
                summary: 'Thiếu thông tin',
                detail: 'Vui lòng đính kèm ít nhất một tệp tin minh chứng!',
                life: 4000
            });
            return;
        }

        onSubmit(e);
    };

    const isAdmin = currentUserInfo?.RoleId === 1 || currentUserInfo?.RoleId === 0;
    const isCreator = !isEditing || (formData.UserId?.toString() === currentUserInfo?.Id?.toString());
    const isMemberLocked = isReadOnly || (isEditing && !isAdmin && !isCreator);

    return (
        <div className="modal-overlay" style={{ zIndex: 1000 }}>
            <div className="modal-box form-modal-box" style={{ maxHeight: '95vh', overflowY: 'auto' }}>

                <div className="modal-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <h3 style={{ margin: 0, paddingRight: '15px', lineHeight: '1.4' }}>
                        {isReadOnly ? "Chi tiết Đăng ký Biên soạn" : (isEditing ? "Cập nhật Đăng ký Biên soạn" : "Thêm mới Đăng ký Biên soạn")}
                    </h3>
                    <button type="button" className="close-btn" onClick={onClose} style={{ fontSize: '26px', lineHeight: '1', display: 'flex', flexShrink: 0, marginTop: '-2px' }}>
                        &times;
                    </button>
                </div>

                <div className="modal-body" style={{ padding: '25px' }}>
                    <form id="bookForm" onSubmit={handleLocalSubmit}>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '15px' }}>
                            <div className="form-group" style={{ flex: '1 1 200px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Loại công trình *</label>
                                <select name="ProjectTypeId" className="form-input" value={formData.ProjectTypeId || ''} onChange={handleChange} required disabled={isReadOnly}>
                                    <option value="">Chọn loại công trình</option>
                                    {projectTypes?.map(pt => <option key={pt.Id} value={pt.Id}>{pt.Name}</option>)}
                                </select>
                            </div>
                            <div className="form-group" style={{ flex: '2 1 300px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Tên sách, giáo trình *</label>
                                <input
                                    type="text"
                                    name="BookName"
                                    className="form-input"
                                    value={formData.BookName || ''}
                                    onChange={handleChange}
                                    onBlur={handleTitleBlur}
                                    required
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Đối tượng sử dụng</label>
                            <textarea name="TargetAudience" className="form-input" rows="2" value={formData.TargetAudience || ''} onChange={handleChange} disabled={isReadOnly} style={{ resize: 'vertical' }}></textarea>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Mục tiêu</label>
                            <textarea name="Objectives" className="form-input" rows="2" value={formData.Objectives || ''} onChange={handleChange} disabled={isReadOnly} style={{ resize: 'vertical' }}></textarea>
                        </div>

                        <div className="form-group" style={{ marginBottom: '15px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Dự kiến cấu trúc (số tiết, số chương, số trang)</label>
                            <textarea name="EstimatedStructure" className="form-input" rows="2" value={formData.EstimatedStructure || ''} onChange={handleChange} disabled={isReadOnly} style={{ resize: 'vertical' }}></textarea>
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '15px', alignItems: 'flex-end' }}>
                            <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px', flex: '1 1 150px' }}>
                                <input type="checkbox" name="IsFirstTime" checked={formData.IsFirstTime || false} onChange={handleChange} style={{ width: '18px', height: '18px' }} disabled={isReadOnly} />
                                <label style={{ margin: 0, fontWeight: 'bold' }}>Biên soạn lần đầu</label>
                            </div>
                            <div className="form-group" style={{ flex: '1 1 150px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: formData.IsFirstTime ? '#aaa' : '#333' }}>Năm XB giáo trình cũ</label>
                                <input type="number" name="OldPublishYear" className="form-input" value={formData.OldPublishYear || ''} onChange={handleChange} disabled={formData.IsFirstTime || isReadOnly} />
                            </div>
                            <div className="form-group" style={{ flex: '2 1 250px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: formData.IsFirstTime ? '#aaa' : '#333' }}>Lý do biên soạn lại</label>
                                <input type="text" name="ReasonForRewrite" className="form-input" value={formData.ReasonForRewrite || ''} onChange={handleChange} disabled={formData.IsFirstTime || isReadOnly} />
                            </div>
                        </div>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '15px' }}>
                            <div className="form-group" style={{ flex: '1 1 200px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Từ ngày *</label>
                                <Calendar
                                    id="StartDate"
                                    name="StartDate"
                                    value={(formData.StartDate && !isNaN(new Date(formData.StartDate).getTime())) ? new Date(formData.StartDate) : null}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('StartDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    disabled={isReadOnly}
                                    required
                                />
                            </div>
                            <div className="form-group" style={{ flex: '1 1 200px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Đến ngày *</label>
                                <Calendar
                                    id="EndDate"
                                    name="EndDate"
                                    value={(formData.EndDate && !isNaN(new Date(formData.EndDate).getTime())) ? new Date(formData.EndDate) : null}
                                    dateFormat="dd/mm/yy"
                                    showIcon
                                    onChange={(e) => handleDateChange('EndDate', e.value)}
                                    style={{ width: '100%' }}
                                    appendTo={document.body}
                                    panelStyle={{ marginTop: '5px', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', borderRadius: '8px', zIndex: 99999 }}
                                    inputStyle={{ padding: '10px 12px', border: '1px solid #cbd5e1', borderRight: 'none', borderRadius: '6px 0 0 6px', fontSize: '14px', outline: 'none' }}
                                    disabled={isReadOnly}
                                    required
                                />
                            </div>
                            <div className="form-group" style={{ flex: '1 1 200px' }}>
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Số tháng thực hiện *</label>
                                <input
                                    type="number"
                                    name="EstimatedMonths"
                                    className="form-input"
                                    value={formData.EstimatedMonths || ''}
                                    onChange={handleChange}
                                    required
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>

                        <div className="form-grid-2" style={{ marginBottom: '15px' }}>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Mã học phần</label>
                                <input type="text" name="CourseCode" className="form-input" value={formData.CourseCode || ''} onChange={handleChange} disabled={isReadOnly} />
                            </div>
                            <div className="form-group">
                                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Số tín chỉ</label>
                                <input type="number" name="CreditCount" className="form-input" value={formData.CreditCount || ''} onChange={handleChange} disabled={isReadOnly} />
                            </div>
                        </div>

                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '10px' }}>
                                <label style={{ fontWeight: 'bold', margin: 0 }}>Danh sách thành viên THUỘC TRƯỜNG *</label>
                                {!isMemberLocked && (
                                    <button
                                        type="button"
                                        onClick={handleAddMember}
                                        style={{
                                            margin: 0,
                                            padding: '6px 14px',
                                            backgroundColor: '#28a745',
                                            color: '#fff',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '5px'
                                        }}
                                    >
                                        <i className="fa-solid fa-user-plus"></i> Chọn người dùng
                                    </button>
                                )}
                            </div>
                            <div style={{ overflowX: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                                <table className="custom-table" style={{ width: '100%', minWidth: '1100px', margin: 0, border: 'none' }}>
                                    <thead style={{ backgroundColor: '#f9f9f9' }}>
                                        <tr>
                                            <th width="5%" style={{ textAlign: 'center' }}>STT</th>
                                            <th width="25%">Thành viên</th>
                                            <th width="20%">Vai trò</th>
                                            <th width="15%">Chức danh</th>
                                            <th width="15%">Trình độ</th>
                                            <th width="25%">Nội dung phân công</th>
                                            {!isMemberLocked && <th width="5%" style={{ textAlign: 'center' }}>Xóa</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {members.length === 0 ? (
                                            <tr><td colSpan={isMemberLocked ? "6" : "7"} style={{ textAlign: 'center', padding: '15px', color: '#999' }}>Chưa có thành viên nào</td></tr>
                                        ) : members.map((m, i) => (
                                            <tr key={i}>
                                                <td style={{ textAlign: 'center' }}>{i + 1}</td>
                                                <td>
                                                    <SearchableFocusDropdown
                                                        value={m.userId}
                                                        options={userOptions}
                                                        onChange={(e) => handleMemberSelect(i, e.value)}
                                                        placeholder="Chọn thành viên"
                                                        panelUniqueClass={`custom-dropdown-panel-compilation-member-${i}`}
                                                        disabled={isMemberLocked}
                                                        itemTemplate={userItemTemplate}
                                                    />
                                                </td>
                                                <td>
                                                    <select
                                                        className="form-input"
                                                        value={m.role || ''}
                                                        onChange={(e) => handleMemberChange(i, 'role', e.target.value)}
                                                        disabled={isMemberLocked}
                                                        style={{ width: '100%', cursor: isMemberLocked ? 'not-allowed' : 'pointer' }}
                                                        required
                                                    >
                                                        <option value="" disabled>Chọn vai trò</option>
                                                        <option value="Chủ biên">Chủ biên</option>
                                                        <option value="Đồng chủ biên">Đồng chủ biên</option>
                                                        <option value="Thành viên">Thành viên</option>
                                                    </select>
                                                </td>
                                                <td><input type="text" className="form-input" value={m.title || ''} readOnly style={{ border: 'none', background: 'transparent' }} /></td>
                                                <td><input type="text" className="form-input" value={m.degree || ''} readOnly style={{ border: 'none', background: 'transparent' }} /></td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="form-input"
                                                        placeholder="Nhập phân công"
                                                        value={m.content || ''}
                                                        onChange={(e) => handleMemberChange(i, 'content', e.target.value)}
                                                        disabled={isMemberLocked}
                                                        style={{ border: 'none', background: isMemberLocked ? '#f8fafc' : 'transparent' }}
                                                    />
                                                </td>
                                                {!isMemberLocked && (
                                                    <td style={{ textAlign: 'center' }}>
                                                        <i className="fa-solid fa-xmark" style={{ color: '#e74c3c', cursor: 'pointer', fontSize: '16px' }} onClick={() => handleRemoveMember(i)} title="Xóa thành viên"></i>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="form-group">
                            <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Tệp tin minh chứng *</label>

                            <p style={{ color: '#e74c3c', fontStyle: 'italic', marginBottom: '15px', marginTop: '-5px', fontSize: '14px' }}>
                                * Chú ý: Hệ thống chỉ nhận tệp minh chứng ở định dạng PDF, JPG/JPEG và PNG. Dung lượng tối đa 5MB / tệp
                            </p>

                            {parsedFiles.length > 0 && (
                                <div style={{ overflowX: 'auto', marginBottom: '15px' }}>
                                    <table className="custom-table" style={{ width: '100%', minWidth: '500px', margin: 0, border: '1px solid #eee' }}>
                                        <thead style={{ backgroundColor: '#f9f9f9' }}>
                                            <tr>
                                                <th width="50px" style={{ textAlign: 'center' }}>TT</th>
                                                <th>Mô tả</th>
                                                <th width="35%">Minh chứng</th>
                                                {!isReadOnly && <th width="80px" style={{ textAlign: 'center' }}>Chức năng</th>}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {parsedFiles.map((file, idx) => (
                                                <tr key={idx}>
                                                    <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                                                    <td>
                                                        <input
                                                            type="text"
                                                            className="form-input"
                                                            placeholder="Nhập thông tin mô tả"
                                                            value={file.description || ''}
                                                            onChange={(e) => handleFileDescChange(idx, e.target.value)}
                                                            style={{ width: '100%', padding: '8px', border: 'none', background: 'transparent', borderBottom: '1px solid #eee', borderRadius: 0 }}
                                                            disabled={isReadOnly}
                                                        />
                                                    </td>
                                                    <td>
                                                        <span onClick={() => downloadIndividualFile(file)} style={{ color: '#3498db', cursor: 'pointer', wordBreak: 'break-all' }} title="Nhấn để xem trước">{file.name}</span>
                                                    </td>
                                                    {!isReadOnly && (
                                                        <td style={{ textAlign: 'center' }}>
                                                            <button type="button" onClick={() => removeEvidenceFile(idx)} style={{ background: '#fff', border: '1px solid #e74c3c', color: '#e74c3c', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}>
                                                                <i className="fa-solid fa-trash-can"></i>
                                                            </button>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {!isReadOnly && (
                                <div style={{ border: '2px dashed #3498db', borderRadius: '8px', padding: '30px', textAlign: 'center', backgroundColor: '#ebf5fb', position: 'relative', overflow: 'hidden' }}>
                                    <input
                                        type="file"
                                        multiple
                                        accept=".pdf, .jpg, .jpeg, .png, application/pdf, image/jpeg, image/png"
                                        onChange={handleMultiFileChange}
                                        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 10 }}
                                        title="Nhấn để chọn tệp"
                                    />
                                    {parsedFiles.length > 0 ? (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '35px', color: '#3498db', marginBottom: '10px' }}></i>
                                            <p style={{ margin: '0 0 10px 0', color: '#003399', fontWeight: 'bold', fontSize: '15px' }}>Đã có {parsedFiles.length} tệp minh chứng</p>
                                            <button type="button" style={{ padding: '6px 15px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', color: '#333' }}>Thêm tệp tin khác</button>
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa-solid fa-cloud-arrow-up" style={{ fontSize: '40px', color: '#3498db', marginBottom: '10px' }}></i>
                                            <p style={{ margin: '0 0 10px 0', color: '#003399', fontWeight: 'bold', fontSize: '15px' }}>Chưa có tệp nào được chọn</p>
                                            <button type="button" style={{ padding: '6px 15px', backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', color: '#333' }}>Chọn tệp từ thiết bị</button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="form-group" style={{ marginTop: '5%' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Ghi chú</label>
                            <textarea name="Notes" className="form-input" rows="2" value={formData.Notes || ''} onChange={handleChange} disabled={isReadOnly} style={{ resize: 'vertical' }}></textarea>
                        </div>
                    </form>
                </div>

                <div className="modal-footer" style={{ borderTop: '1px solid #eee', padding: '15px 25px' }}>
                    <button type="button" className="btn-cancel" onClick={onClose}>
                        {isReadOnly ? "Đóng" : "Hủy"}
                    </button>
                    {!isReadOnly && (
                        <button type="submit" form="bookForm" className="btn-submit">Hoàn tất</button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default BookCompilationForm;